import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ChevronLeft, Search, Filter,
    RefreshCw, ArrowUpDown, ChevronRight, ChevronsLeft, ChevronsRight, FileText,
    Columns3, Check, Download, X
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { CustomDropdown } from '../../components/CustomDropdown ';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import { useClientLevel } from '../../hooks/useClientLevel';

interface FilterConfig {
    key: string;
    label: string;
    type: 'date_range' | 'dropdown' | 'string' | 'multi_select_dropdown';
    field: string;
    options?: { label: string; value?: any; id?: any; selected?: boolean }[];
}

interface ColumnConfig {
    key: string;
    label: string;
    type: string;
    sortable: boolean;
    default: boolean;
    colors?: Record<string, string>;
}

interface ReportConfig {
    report_key: string;
    title: string;
    description: string;
    filters: FilterConfig[];
    quick_filters: { key: string; label: string }[];
    columns: ColumnConfig[];
    formats: string[];
}

const ReportViewer: React.FC = () => {
    const { reportKey } = useParams<{ reportKey: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const level = useClientLevel();

    const [config, setConfig] = useState<ReportConfig | null>(null);
    const [data, setData] = useState<any[]>([]);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(false);

    // API Parameters State
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
    const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
    const [dateFilter, setDateFilter] = useState<{ field: string; start: string; end: string } | null>(null);
    const [orderBy, setOrderBy] = useState<string>('');
    const [orderDir, setOrderDir] = useState<'ASC' | 'DESC'>('DESC');

    const [searchTerm, setSearchTerm] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);

    // UI State for Panels
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [columnDropdownPos, setColumnDropdownPos] = useState({ top: 0, right: 0 });
    const columnButtonRef = useRef<HTMLButtonElement>(null);

    // AbortController for cancelling export
    const abortControllerRef = useRef<AbortController | null>(null);

    // Pagination from URL
    const limit = parseInt(searchParams.get('limit') || '10');
    const currentPage = parseInt(searchParams.get('page') || '1');
    const offset = (currentPage - 1) * limit;

    const fetchConfig = async (clientIds?: string) => {
        setIsLoadingConfig(config === null); // Only show full loader on first flight
        try {
            const params: any = {};
            if (clientIds) {
                params.client_id = clientIds;
            } else if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }

            const response = await baseapi.get(`/reports/${reportKey}/config`, { params });
            if (response.data.status) {
                const configData = response.data.data;

                // Initialize active filters based on 'selected' flags from API
                const initialFilters: Record<string, any> = {};
                configData.filters.forEach((filter: any) => {
                    const selectedOptions = filter.options
                        ?.filter((opt: any) => opt.selected)
                        .map((opt: any) => (opt.value ?? opt.id)?.toString());

                    if (selectedOptions && selectedOptions.length > 0) {
                        initialFilters[filter.field] = filter.type === 'multi_select_dropdown'
                            ? selectedOptions
                            : selectedOptions[0];
                    }
                });

                setConfig(configData);
                setActiveFilters(prev => ({ ...prev, ...initialFilters }));
                setSelectedColumns(configData.columns.filter((c: ColumnConfig) => c.default).map((c: ColumnConfig) => c.key));

                // Set default order by if available
                if (configData.columns.length > 0 && !orderBy) {
                    const defaultSort = configData.columns.find((c: ColumnConfig) => c.sortable);
                    if (defaultSort) setOrderBy(defaultSort.key);
                }
            }
        } catch (error) {
            showError('Failed to load report configuration');
            if (config === null) navigate('/reports');
        } finally {
            setIsLoadingConfig(false);
        }
    };

    const fetchData = useCallback(async () => {
        if (!reportKey) return;
        setIsLoadingData(true);
        try {
            const payload: any = {
                limit,
                offset,
                timezone: 'Asia/Kolkata'
            };

            if (level.name !== 'global' && level.id) {
                payload.client_id = level.id;
            }

            // Only add filters if they have values
            const cleanFilters = Object.entries(activeFilters).reduce((acc, [key, val]) => {
                if (val !== undefined && val !== null && val !== '') {
                    acc[key] = val;
                }
                return acc;
            }, {} as Record<string, any>);

            if (level.name !== 'global' && level.id) {
                cleanFilters.client_id = level.id;
            }

            if (Object.keys(cleanFilters).length > 0) payload.filters = JSON.stringify(cleanFilters);
            if (activeQuickFilter) payload.quick_filter = activeQuickFilter;
            if (dateFilter) payload.date_filter = JSON.stringify(dateFilter);
            if (orderBy) payload.order_by = orderBy;
            if (orderDir) payload.order_dir = orderDir;
            if (searchTerm) payload.search = searchTerm;

            const response = await baseapi.get(`/reports/${reportKey}/data`, { params: payload });
            if (response.data.status) {
                setData(response.data.data);
                setTotalRecords(response.data.total || response.data.data.length);
            }
        } catch (error) {
            showError('Failed to fetch report data');
        } finally {
            setIsLoadingData(false);
        }
    }, [reportKey, limit, offset, activeFilters, activeQuickFilter, dateFilter, orderBy, orderDir, searchTerm, level]);

    const handleExport = async (format: string) => {
        if (!reportKey) return;

        setIsExporting(true);
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const params: any = {
                timezone: 'Asia/Kolkata',
                format: format
            };
            if (level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }

            // Only add filters if they have values
            const cleanFilters = Object.entries(activeFilters).reduce((acc, [key, val]) => {
                if (val !== undefined && val !== null && val !== '') {
                    acc[key] = val;
                }
                return acc;
            }, {} as Record<string, any>);

            if (level.name !== 'global' && level.id) {
                cleanFilters.client_id = level.id;
            }

            if (Object.keys(cleanFilters).length > 0) params.filters = cleanFilters;
            if (activeQuickFilter) params.quick_filter = activeQuickFilter;
            if (dateFilter) params.date_filter = dateFilter;
            if (orderBy) params.order_by = [{ field: orderBy, direction: orderDir }];
            if (searchTerm) params.search = searchTerm;

            const response = await baseapi.post(`/reports/${reportKey}/export`, params, {
                responseType: 'blob',
                signal: controller.signal // Attach the abort signal
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const extension = format === 'excel' ? 'xlsx' : format;
            const now = new Date();
            const pad = (n: number) => String(n).padStart(2, '0');
            const timestamp = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}`;
            link.setAttribute('download', `${reportKey}_${timestamp}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            if (error.name === 'CanceledError' || error.message === 'canceled') {
                console.log('Export request was cancelled.');
                // Optionally show a toast for cancellation
            } else {
                showError(`Failed to export ${format.toUpperCase()} report`);
            }
        } finally {
            setIsExporting(false);
            abortControllerRef.current = null;
        }
    };

    const handleCancelExport = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort(); // Cancel the API call
            abortControllerRef.current = null;
        }
        setIsExporting(false);
    };

    const updatePage = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const updateLimit = (newLimit: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('limit', newLimit.toString());
        params.set('page', '1'); // Reset to page 1 on limit change
        setSearchParams(params);
    };

    useEffect(() => {
        fetchConfig();
    }, [reportKey, level]);

    useEffect(() => {
        if (config) {
            fetchData();
        }
    }, [fetchData, config]);

    if (isLoadingConfig) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-slate-400">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-orange-500 animate-pulse" />
                    </div>
                </div>
                <p className="mt-6 text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">Initializing Report Engine</p>
            </div>
        );
    }

    if (!config) return null;

    const handleSort = (columnKey: string) => {
        if (orderBy === columnKey) {
            setOrderDir(orderDir === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setOrderBy(columnKey);
            setOrderDir('DESC');
        }
        updatePage(1);
    };

    const renderCell = (row: any, column: ColumnConfig) => {
        const value = row[column.label] ?? row[column.key];

        if (column.type === 'datetime') {
            return value ? new Date(value).toLocaleString() : '-';
        }

        const displayValue = typeof value === 'string'
            ? value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : value?.toString() || '-';

        if (column.colors && value && typeof value === 'string') {
            const color = column.colors[value.toLowerCase()];
            if (color) {
                return (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${color}20`, color: color, border: `1px solid ${color}40` }}>
                        {displayValue}
                    </span>
                );
            }
        }

        return displayValue;
    };

    return (
        <div className="flex flex-col min-h-screen text-slate-200 font-sans">
            {/* Top Navigation / Header */}
            <div className="flex items-center justify-between px-6 py-2 bg-slate-900/50 backdrop-blur-md border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/reports')} className="p-1.5 bg-orange-600 rounded-md transition-colors text-slate-300 hover:text-white">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div>
                        <h1 className="text-sm font-bold text-white tracking-tight leading-none uppercase">{config.title}</h1>
                        <p className="text-[10px] text-slate-300 font-medium mt-1 leading-none">{config.description}</p>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col px-6 pt-4 pb-24 gap-4">

                {/* 1. Quick Filters Row (Pills) */}
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 shrink-0">
                    {config.quick_filters.map((qf) => (
                        <button
                            key={qf.key}
                            onClick={() => {
                                setActiveQuickFilter(activeQuickFilter === qf.key ? null : qf.key);
                                updatePage(1);
                            }}
                            className={`px-3 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap transition-all ${activeQuickFilter === qf.key
                                ? 'bg-orange-600 border-orange-500 text-white shadow-lg shadow-orange-950/20'
                                : 'bg-slate-700 border-slate-700 text-slate-200 hover:border-slate-600 hover:bg-slate-800'
                                }`}
                        >
                            {qf.label}
                        </button>
                    ))}
                </div>

                {/* 2. Action Bar Card */}
                <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-2.5 flex items-center justify-end gap-3 backdrop-blur-sm shrink-0">
                    <button
                        onClick={() => fetchData()}
                        disabled={isLoadingData}
                        className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-xs font-bold text-slate-300 hover:bg-slate-800 transition-all flex items-center gap-2 shrink-0 group"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-slate-300 group-hover:text-orange-500 ${isLoadingData ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <div className="relative max-w-[240px] group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-orange-500 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search ..."
                            className="w-full bg-slate-950/50 border border-slate-800/80 rounded-lg pl-9 pr-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500/50 transition-all placeholder:text-slate-200 font-medium"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                updatePage(1);
                            }}
                        />
                    </div>



                    <div className="flex items-center gap-2 shrink-0 pl-4 relative">
                        <div className="relative">
                            <button
                                ref={columnButtonRef}
                                onClick={() => {
                                    if (!showColumnDropdown && columnButtonRef.current) {
                                        const rect = columnButtonRef.current.getBoundingClientRect();
                                        setColumnDropdownPos({
                                            top: rect.bottom,
                                            right: window.innerWidth - rect.right
                                        });
                                    }
                                    setShowColumnDropdown(!showColumnDropdown);
                                    if (showFilterPanel) setShowFilterPanel(false);
                                }}
                                className={`px-3 py-1.5 rounded-lg border text-[11px] font-bold flex items-center gap-2 transition-all group ${showColumnDropdown
                                    ? 'bg-orange-600 border-orange-500 text-white shadow-lg shadow-orange-950/20'
                                    : 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800'
                                    }`}
                            >
                                <Columns3 className={`w-3.5 h-3.5 ${!showColumnDropdown && 'text-slate-300 group-hover:text-orange-400'}`} />
                                Columns
                            </button>

                            {/* Column Visibility Dropdown Portal */}
                            {showColumnDropdown && createPortal(
                                <>
                                    <div className="fixed inset-0 z-[1000]" onClick={() => setShowColumnDropdown(false)} />
                                    <div
                                        style={{
                                            position: 'fixed',
                                            top: `${columnDropdownPos.top + 8}px`,
                                            right: `${columnDropdownPos.right}px`,
                                        }}
                                        className="min-w-[220px] bg-slate-950 border border-slate-800 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.9)] z-[1001] py-2 animate-in fade-in zoom-in-95 duration-200"
                                    >
                                        <div className="px-4 py-2 border-b border-slate-800/50 mb-1">
                                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest leading-none">Display Columns</span>
                                        </div>
                                        <div className="max-h-84 overflow-y-auto no-scrollbar py-1">
                                            {config.columns.map((col) => {
                                                const isSelected = selectedColumns.includes(col.key);
                                                return (
                                                    <button
                                                        key={col.key}
                                                        onClick={() => {
                                                            if (isSelected) {
                                                                if (selectedColumns.length > 1) {
                                                                    setSelectedColumns(selectedColumns.filter(k => k !== col.key));
                                                                }
                                                            } else {
                                                                setSelectedColumns([...selectedColumns, col.key]);
                                                            }
                                                        }}
                                                        className="w-full px-4 py-2 flex items-center justify-between group hover:bg-slate-900 transition-colors"
                                                    >
                                                        <span className={`text-[12px] font-semibold transition-colors ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                                                            {col.label}
                                                        </span>
                                                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${isSelected
                                                            ? 'bg-orange-600 border-orange-500 shadow-[0_0_10px_rgba(234,88,12,0.3)]'
                                                            : 'border-slate-700 bg-slate-900'
                                                            }`}>
                                                            {isSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>,
                                document.body
                            )}
                        </div>

                        {config.filters.length > 0 && (
                            <button
                                onClick={() => {
                                    setShowFilterPanel(!showFilterPanel);
                                    if (showColumnDropdown) setShowColumnDropdown(false);
                                }}
                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-2 transition-all border ${showFilterPanel
                                    ? 'bg-orange-600 border-orange-500 text-white shadow-lg shadow-orange-950/20'
                                    : 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800'
                                    }`}
                            >
                                <Filter className={`w-3.5 h-3.5 ${!showFilterPanel && 'text-slate-300'}`} />
                                Filters
                            </button>
                        )}

                        <div className="h-6 w-[1px] bg-slate-800 mx-2" />

                        <div className="flex items-center gap-2">
                            {config.formats?.map((format) => (
                                <button
                                    key={format}
                                    onClick={() => handleExport(format)}
                                    className="w-8 h-8 flex items-center justify-center group overflow-hidden"
                                    title={`Export ${format.toUpperCase()}`}
                                >
                                    <img
                                        src={format.toLowerCase() === 'excel' ? '/xls.png' : `/${format.toLowerCase()}.png`}
                                        alt={format.toUpperCase()}
                                        className={`w-6 ${format.toLowerCase() === 'csv' ? 'h-6' : 'h-7'} object-contain`}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 3. Expandable Filter Panel */}
                {showFilterPanel && (
                    <div className="relative z-20 bg-slate-800/80 border border-slate-800/60 rounded-xl p-3 backdrop-blur-sm animate-in fade-in slide-in-from-top-2 duration-200 shrink-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 space-y-2">
                            {config.filters
                                .filter(f => level.name === 'global' || f.field !== 'client_id')
                                .map((filter) => (
                                    <div key={filter.key} className="flex flex-col gap-1.5">
                                        <label className="text-[9px] font-black text-slate-300 uppercase tracking-widest leading-none mb-0.5">{filter.label?.replace(/_/g, ' ')}</label>
                                        {filter.type === 'dropdown' ? (
                                            <CustomDropdown
                                                data={filter.options?.map(opt => ({ label: opt.label?.replace(/_/g, ' '), value: (opt.value ?? opt.id)?.toString() ?? '' })) || []}
                                                selectedValue={activeFilters[filter.field]?.toString() || ''}
                                                onSelect={(val) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: val || undefined }));
                                                    updatePage(1);
                                                }}
                                                width="w-full"
                                                placeholder={`Select ${filter.label}`}
                                                buttonHeight={8}
                                                className="!bg-slate-950/40 !border-slate-800/80 rounded-lg"
                                            />
                                        ) : filter.type === 'multi_select_dropdown' ? (
                                            <MultiSelectDropdown
                                                options={filter.options?.map(opt => ({ label: opt.label?.replace(/_/g, ' '), value: (opt.value ?? opt.id)?.toString() ?? '' })) || []}
                                                value={Array.isArray(activeFilters[filter.field]) ? activeFilters[filter.field] : []}
                                                onChange={(val) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: val && val.length > 0 ? val : undefined }));
                                                    updatePage(1);
                                                    if (filter.field === 'client_id') {
                                                        fetchConfig(val.join(','));
                                                    }
                                                }}
                                                width="w-full"
                                                placeholder={`Select ${filter.label}`}
                                                className="!bg-slate-950/40 !border-slate-800/80 rounded-lg"
                                            />
                                        ) : filter.type === 'date_range' ? (
                                            <div className="flex items-center gap-1.5">
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="date"
                                                        className="w-full h-8 bg-slate-950/40 border border-slate-800/80 rounded-lg px-2 text-[10px] font-bold text-slate-300 focus:outline-none focus:border-orange-500/50 cursor-pointer hover:bg-slate-900 transition-colors "
                                                        value={dateFilter?.field === filter.field ? dateFilter.start : ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setDateFilter(prev => {
                                                                const field = filter.field;
                                                                const end = prev?.field === field ? prev.end : '';
                                                                return val || end ? { field, start: val, end } : null;
                                                            });
                                                            updatePage(1);
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-slate-700 font-bold text-[10px]">-</div>
                                                <div className="flex-1 relative">
                                                    <input
                                                        type="date"
                                                        className="w-full h-8 bg-slate-950/40 border border-slate-800/80 rounded-lg px-2 text-[10px] font-bold text-slate-300 focus:outline-none focus:border-orange-500/50 cursor-pointer hover:bg-slate-900 transition-colors "
                                                        value={dateFilter?.field === filter.field ? dateFilter.end : ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setDateFilter(prev => {
                                                                const field = filter.field;
                                                                const start = prev?.field === field ? prev.start : '';
                                                                return start || val ? { field, start, end: val } : null;
                                                            });
                                                            updatePage(1);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder={`Filter ${filter.label}...`}
                                                className="w-full h-8 bg-slate-950/40 border border-slate-800/80 rounded-lg px-3 text-[11px] font-bold text-white focus:outline-none focus:border-orange-500/50 placeholder:text-slate-700"
                                                value={activeFilters[filter.field] || ''}
                                                onChange={(e) => {
                                                    setActiveFilters(prev => ({ ...prev, [filter.field]: e.target.value || undefined }));
                                                    updatePage(1);
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            <div className="flex items-end h-full">
                                {Object.keys(activeFilters).length > 0 || dateFilter ? (
                                    <button
                                        onClick={() => {
                                            setActiveFilters({});
                                            setDateFilter(null);
                                            updatePage(1);
                                        }}
                                        className="h-8 text-[10px] font-black text-rose-500 hover:text-rose-400 uppercase tracking-widest transition-colors flex items-center px-2 hover:bg-rose-500/10 rounded-lg"
                                    >
                                        Clear All
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}

                {/* 4. Table Area */}
                <div className="flex-1 min-h-0 pb-4">
                    <div className="bg-slate-900/50 rounded-xl border border-slate-800 shadow-xl flex flex-col overflow-hidden">
                        <div className="flex-1 relative min-h-0 overflow-x-auto custom-scroll">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-800/80 border-b border-slate-800 shadow-sm sticky top-0 z-10">
                                    <tr>
                                        {config.columns.filter(c => selectedColumns.includes(c.key)).map((col, idx) => (
                                            <th
                                                key={col.key}
                                                className={`px-4 py-2.5 text-[10px] text-slate-200 uppercase tracking-widest whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-white transition-colors group' : ''} ${idx === 0 ? 'pl-6' : ''}`}
                                                onClick={() => col.sortable && handleSort(col.key)}
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    {col.label?.replace(/_/g, ' ')}
                                                    {col.sortable && (
                                                        <ArrowUpDown className={`w-3 h-3 ${orderBy === col.key ? 'text-orange-500' : 'text-slate-600 group-hover:text-slate-400'}`} />
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/60 bg-slate-900/20">
                                    {isLoadingData && data.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectedColumns.length} className="py-20 text-center">
                                                <div className="flex flex-col items-center justify-center space-y-3">
                                                    <div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                                    <p className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Syncing Recordset...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : data.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectedColumns.length} className="py-20 text-center">
                                                <div className="flex flex-col items-center justify-center space-y-3 opacity-40">
                                                    <FileText className="w-10 h-10 text-slate-400" />
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">End of results</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        data.map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-800/40 transition-colors group">
                                                {config.columns.filter(c => selectedColumns.includes(c.key)).map((col, cIdx) => (
                                                    <td key={col.key} className={`px-4 py-2.5 text-[11px] font-medium text-slate-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[250px] transition-colors group-hover:text-slate-200 ${cIdx === 0 ? 'pl-6' : ''}`}>
                                                        {renderCell(row, col)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* 5. Pagination / Footer (Inside Table Card) */}
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-800/40 border-t border-slate-800 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Rows</span>
                                    <ItemsPerPageDropdown
                                        value={limit}
                                        onChange={(v) => updateLimit(v)}
                                    />
                                </div>
                                <span className="text-[10px] font-medium text-slate-500 tracking-widest">
                                    <span className="text-slate-300">{offset + 1}-{Math.min(offset + limit, totalRecords)}</span> <span className="mx-1 text-slate-600">OF</span> <span className="text-slate-300 font-bold">{totalRecords}</span>
                                </span>
                            </div>

                            <div className="flex items-center gap-1.5">
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => updatePage(1)}
                                    className="p-1.5 text-slate-400 hover:text-white disabled:opacity-20 transition-colors bg-slate-800/50 hover:bg-slate-700 rounded"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => updatePage(Math.max(1, currentPage - 1))}
                                    className="p-1.5 text-slate-400 hover:text-white disabled:opacity-20 transition-colors bg-slate-800/50 hover:bg-slate-700 rounded"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <div className="px-3 py-1 bg-orange-600/10 border border-orange-500/20 text-orange-500 rounded text-[11px] font-bold mx-1">
                                    {currentPage}
                                </div>
                                <button
                                    disabled={offset + limit >= totalRecords}
                                    onClick={() => updatePage(currentPage + 1)}
                                    className="p-1.5 text-slate-400 hover:text-white disabled:opacity-20 transition-colors bg-slate-800/50 hover:bg-slate-700 rounded"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={offset + limit >= totalRecords}
                                    onClick={() => updatePage(Math.ceil(totalRecords / limit))}
                                    className="p-1.5 text-slate-400 hover:text-white disabled:opacity-20 transition-colors bg-slate-800/50 hover:bg-slate-700 rounded"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Export Loading Overlay */}
            {isExporting && (
                <div className="fixed inset-0 z-[2000] bg-slate-950/60 backdrop-blur-[4px] flex items-center justify-center animate-in fade-in duration-300">
                    <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl flex flex-col items-center gap-4 text-center min-w-[280px]">
                        <div className="relative mb-2">
                            <div className="w-16 h-16 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Download className="w-6 h-6 text-orange-500 animate-bounce" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-1.5">Generating Report</h3>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">
                                Please wait while we gather <br />
                                your recordset ...
                            </p>
                        </div>

                        {/* Cancel Button */}
                        <button
                            onClick={handleCancelExport}
                            className="mt-4 px-5 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white text-[10px] font-bold uppercase tracking-widest rounded-lg border border-slate-700 transition-colors flex items-center gap-1.5"
                        >
                            <X className="w-3.5 h-3.5" />
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportViewer;